// src/app/services/sse.service.ts

import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { RefreshService } from './refresh.service';

export interface SSEEvent {
  type: string;
  data: any;
  timestamp: string;
}

type ConnStatus = 'connected' | 'disconnected' | 'connecting';

@Injectable({
  providedIn: 'root'
})
export class SSEService {
  private eventSource: EventSource | null = null;
  private connectionStatus = new BehaviorSubject<ConnStatus>('disconnected');
  private eventSubject = new BehaviorSubject<SSEEvent | null>(null);

  private reconnectAttempts = 0;
  private maxReconnectAttempts = 8;
  private baseReconnectInterval = 2000; // ms

  private sseUrlBase = environment.sseUrl;

  constructor(private ngZone: NgZone, private refreshService: RefreshService) {
    console.log('[SSE] inicializado, URL base:', this.sseUrlBase);
  }

  get connectionStatus$(): Observable<ConnStatus> {
    return this.connectionStatus.asObservable();
  }

  get events$(): Observable<SSEEvent | null> {
    return this.eventSubject.asObservable();
  }

  connect(): void {
    if (this.eventSource) {
      console.log('[SSE] ya está conectado');
      return;
    }

    const token = this.getTokenFromStorage();
    const url = this.buildUrlWithToken(this.sseUrlBase, token);

    console.log('[SSE] Conectando a:', url);
    this.connectionStatus.next('connecting');

    try {
      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        this.ngZone.run(() => {
          console.log('[SSE] conectado');
          this.connectionStatus.next('connected');
          this.reconnectAttempts = 0;
        });
      };

      this.eventSource.onmessage = (evt) => {
        this.ngZone.run(() => {
          try {
            const raw = JSON.parse(evt.data);
            const sseEvent: SSEEvent = {
              type: raw?.type ?? 'message',
              data: raw?.data ?? raw,
              timestamp: new Date().toISOString()
            };
            this.eventSubject.next(sseEvent);
            this.handleSSEEvent(sseEvent);
          } catch (e) {
            console.error('[SSE] error parseando evento:', e, 'payload:', evt.data);
          }
        });
      };

      this.eventSource.onerror = (err) => {
        this.ngZone.run(() => {
          console.error('[SSE] onerror:', err);
          this.connectionStatus.next('disconnected');
          this.handleReconnection();
        });
      };

    } catch (error) {
      console.error('[SSE] error creando EventSource:', error);
      this.connectionStatus.next('disconnected');
      this.handleReconnection();
    }
  }

  disconnect(): void {
    if (this.eventSource) {
      console.log('[SSE] desconectando…');
      try { this.eventSource.close(); } catch {}
      this.eventSource = null;
    }
    this.connectionStatus.next('disconnected');
  }

  forceReconnect(): void {
    this.reconnectAttempts = 0;
    this.disconnect();
    setTimeout(() => this.connect(), 300);
  }

  isConnected(): boolean {
    return this.connectionStatus.value === 'connected';
  }

  // ============ Internos ============

  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] reintentos agotados');
      return;
    }
    this.reconnectAttempts++;
    const delay = this.baseReconnectInterval * Math.min(this.reconnectAttempts, 5);
    console.log(`[SSE] reintentando conexión (${this.reconnectAttempts}/${this.maxReconnectAttempts}) en ${delay}ms`);

    setTimeout(() => {
      this.disconnect();
      this.connect();
    }, delay);
  }

  private handleSSEEvent(evt: SSEEvent): void {
    switch (evt.type) {
      case 'connected':
      case 'ping':
        break;

      case 'despacho.created':
        this.notifyDespachoCreado(evt.data);
        this.refreshService.triggerRefresh();
        break;

      case 'despacho.geocoded':
        this.notifyDespachoGeocodificado(evt.data);
        break;

      // Backend envía { type: "asistencia", data: { tipo, numeroRegistro, companiaId, ... } }
      case 'asistencia': {
        const tipo = evt.data?.tipo; // 'ingreso' | 'salida'
        if (tipo === 'ingreso' || tipo === 'salida') {
          this.notifyAsistenciaChange(tipo, evt.data);
          this.refreshService.triggerRefresh();
        }
        break;
      }

      // Soporte legacy por si queda en algún lugar del backend
      case 'nueva_asistencia':
        this.notifyAsistenciaChange('ingreso', evt.data);
        this.refreshService.triggerRefresh('sse:nueva_asistencia');
        break;

      case 'salida_asistencia':
        this.notifyAsistenciaChange('salida', evt.data);
        this.refreshService.triggerRefresh('sse:salida_asistencia');
        break;

      case 'disponibles_actualizado':
        this.notifyDisponiblesUpdated(evt.data);
        this.refreshService.triggerRefresh('sse:disponibles_actualizado');
        break;

      default:
        break;
    }
  }

  private notifyDespachoCreado(data: any): void {
    const ev = new CustomEvent('despacho-creado', {
      detail: { data, timestamp: new Date() }
    });
    window.dispatchEvent(ev);
  }

  private notifyDespachoGeocodificado(data: any): void {
    const ev = new CustomEvent('despacho-geocodificado', {
      detail: { data, timestamp: new Date() }
    });
    window.dispatchEvent(ev);
  }

  private notifyAsistenciaChange(tipo: 'ingreso' | 'salida', data: any): void {
    const ev = new CustomEvent('asistencia-actualizada', {
      detail: { tipo, data, timestamp: new Date() }
    });
    window.dispatchEvent(ev);
  }

  private notifyDisponiblesUpdated(data: any): void {
    const ev = new CustomEvent('disponibles-actualizado', {
      detail: { companiaId: data?.compania_id ?? data?.companiaId, count: data?.count, timestamp: new Date() }
    });
    window.dispatchEvent(ev);
  }

  private buildUrlWithToken(base: string, token: string | null): string {
    if (!token) return base;
    const hasQuery = base.includes('?');
    const hasToken = /[?&]token=/.test(base);
    if (hasToken) return base;
    return `${base}${hasQuery ? '&' : '?'}token=${encodeURIComponent(token)}`;
  }

  private getTokenFromStorage(): string | null {
    const keys = ['token', 'jwt', 'JWT', 'auth_token', 'access_token'];
    for (const k of keys) {
      const v = localStorage.getItem(k);
      if (v && v.length > 10) return v.replace(/^"|"$/g, '');
    }
    for (const k of keys) {
      const v = sessionStorage.getItem(k);
      if (v && v.length > 10) return v.replace(/^"|"$/g, '');
    }
    return null;
  }
}
